import { type ChangeEvent, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
    FaArrowLeft,
    FaCalendarAlt,
    FaCheckCircle,
    FaClock,
    FaFileImage,
    FaInfoCircle,
    // FaMapMarkerAlt,
    FaStar,
} from 'react-icons/fa'
import Button from '@/components/common/Button/Button'
import Input from '@/components/forms/Input/Input'
import { useProviderFeed, useAvailableSchedule } from '@/hooks/useServices'
import { useCreateServiceRequest } from '@/hooks/useRequests'
import { useLocation } from '@/hooks/useLocation'
import {
    useCreateQuoteRequest,
    useRequestQuoteVisit,
} from '@/hooks/useQuoteRequests'
import { uploadImageToGCS } from '@/utils/uploadImage'
import toast from 'react-hot-toast'
import routes from '@/routes/routes'
import './ProviderDetail.css'

type ProviderOffering = {
    id: number
    service_id: number
    service_name: string
    category_name?: string
    pricing_type: 'hourly' | 'quote'
    hourly_rate?: number | null
    minimum_hours?: number | null
    base_quote_amount?: number | null
    quote_notes?: string
    requires_quote_visit: boolean
    description?: string
}

type ProviderFromFeed = {
    id: number
    first_name: string
    last_name: string
    avatar_url?: string
    bio?: string
    average_rating: number
    total_ratings: number
    total_services_completed: number
    service_districts: Array<{ id: number; name: string }>
    availability_schedule?: Record<
        string,
        {
            enabled: boolean
            slots: Array<string | { start: string; end: string } | [string, string]>
        }
    >
    service_offerings: ProviderOffering[]
}

const hoursRange = Array.from({ length: 12 }, (_, i) => 8 + i)

const ProviderDetail = () => {
    const { id } = useParams()
    const providerId = Number(id)
    const navigate = useNavigate()
    const { location } = useLocation()

    // Always fetch all providers to ensure the provider is visible regardless of user's district
    const { data: providerFeed, isLoading, isError } = useProviderFeed({
        districtId: undefined,
        allProviders: true,
    })

    const provider: ProviderFromFeed | undefined = useMemo(() => {
        if (!providerFeed?.providers) return undefined
        return providerFeed.providers.find((item: ProviderFromFeed) => item.id === providerId)
    }, [providerFeed, providerId])

    const [selectedServiceId, setSelectedServiceId] = useState<number | null>(null)
    const selectedService = useMemo<ProviderOffering | null>(() => {
        if (!provider?.service_offerings?.length) return null
        if (selectedServiceId == null) return provider.service_offerings[0]
        return provider.service_offerings.find((service) => service.id === selectedServiceId) ?? provider.service_offerings[0]
    }, [provider?.service_offerings, selectedServiceId])

    useEffect(() => {
        if (provider?.service_offerings?.length) {
            setSelectedServiceId(provider.service_offerings[0].id)
        }
    }, [provider?.id, provider?.service_offerings])

    const [selectedDate, setSelectedDate] = useState<string>('')
    const [selectedHours, setSelectedHours] = useState<number[]>([])
    const [serviceDescription, setServiceDescription] = useState('')

    const [quoteDescription, setQuoteDescription] = useState('')
    const [quoteFiles, setQuoteFiles] = useState<File[]>([])
    const [quoteRequestVisit, setQuoteRequestVisit] = useState(false)
    const [quoteVisitNotes, setQuoteVisitNotes] = useState('')
    const [quoteSubmitting, setQuoteSubmitting] = useState(false)

    useEffect(() => {
        setSelectedDate('')
        setSelectedHours([])
        setServiceDescription('')
        setQuoteDescription('')
        setQuoteFiles([])
        setQuoteVisitNotes('')
        setQuoteRequestVisit(Boolean(selectedService?.requires_quote_visit))
    }, [selectedService?.id, selectedService?.requires_quote_visit])

    const { mutate: createServiceRequest, isPending: isCreatingRequest } = useCreateServiceRequest()
    const createQuoteRequest = useCreateQuoteRequest()
    const requestQuoteVisit = useRequestQuoteVisit()

    // Fetch available schedule to check blocked hours
    // Always fetch from today, not from selectedDate, to ensure we have data when user selects a date
    const today = new Date().toISOString().split('T')[0]
    const endDate = new Date(new Date(today).getTime() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
    const { data: availableSchedule, refetch: refetchSchedule, isLoading: scheduleLoading } = useAvailableSchedule(
        selectedService?.id ?? null,
        today,
        endDate
    )

    // Refetch schedule when service changes
    useEffect(() => {
        if (selectedService?.id) {
            refetchSchedule()
        }
    }, [selectedService?.id, refetchSchedule])


    const handleHourToggle = (hour: number) => {
        setSelectedHours((prev) => {
            if (prev.includes(hour)) {
                return prev.filter((value) => value !== hour)
            }

            if (prev.length === 0) {
                return [hour]
            }

            const maxSelected = Math.max(...prev)
            if (hour === maxSelected + 1) {
                return [...prev, hour]
            }

            toast.error('Selecciona horas consecutivas')
            return prev
        })
    }

    const isHourAvailable = (hour: number) => {
        // Log the full availability schedule structure once
        if (hour === 8 && provider?.availability_schedule) {
            console.log(`[isHourAvailable] Full availability_schedule:`, provider.availability_schedule)
            console.log(`[isHourAvailable] Availability schedule keys:`, Object.keys(provider.availability_schedule))
        }
        
        console.log(`[isHourAvailable] Checking hour ${hour}`, {
            selectedDate,
            selectedHours,
            hasProvider: !!provider,
            hasAvailabilitySchedule: !!provider?.availability_schedule,
            scheduleLoading,
            hasAvailableSchedule: !!availableSchedule,
        })

        // If no date selected, allow all hours (will be validated when date is selected)
        if (!selectedDate) {
            console.log(`[isHourAvailable] No date selected, allowing hour ${hour}`)
            return true
        }
        
        // If no provider schedule, allow all hours (will be validated on submission)
        if (!provider?.availability_schedule) {
            console.log(`[isHourAvailable] No provider schedule, allowing hour ${hour}`)
            return true
        }
        
        const date = new Date(selectedDate + 'T00:00:00') // Ensure we're using the correct date (avoid timezone issues)
        // Use the same format as backend: strftime('%A').lower()
        // toLocaleDateString with 'en-US' and weekday: 'long' gives full weekday name
        const dayKey = date.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase()
        const daySchedule = provider.availability_schedule[dayKey]
        
        // Also try alternative day key formats in case of mismatch
        const dayKeyAlt1 = date.toLocaleDateString('en-US', { weekday: 'short' }).toLowerCase()
        const dayKeyAlt2 = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][date.getDay()]
        
        console.log(`[isHourAvailable] Day schedule check`, {
            selectedDate,
            dateObject: date,
            dayOfWeek: date.getDay(),
            dayKey,
            dayKeyAlt1,
            dayKeyAlt2,
            allScheduleKeys: Object.keys(provider.availability_schedule),
            daySchedule,
            enabled: daySchedule?.enabled,
            slotsLength: daySchedule?.slots?.length,
            dayScheduleExists: dayKey in provider.availability_schedule,
        })
        
        // If day is not enabled in provider's schedule, block all hours
        if (!daySchedule?.enabled || !daySchedule.slots?.length) {
            console.log(`[isHourAvailable] Day not enabled or no slots, blocking hour ${hour}`)
            return false
        }

        // Check if hour is in provider's availability schedule
        const hourInSlot = daySchedule.slots.some((slot) => {
            let start: string
            let end: string

            if (typeof slot === 'string') {
                ;[start, end] = slot.split('-')
            } else if (Array.isArray(slot)) {
                ;[start, end] = slot
            } else {
                start = slot.start
                end = slot.end
            }

            const startHour = parseInt(start.split(':')[0], 10)
            const endHour = parseInt(end.split(':')[0], 10)
            const inSlot = hour >= startHour && hour < endHour
            
            console.log(`[isHourAvailable] Checking slot`, {
                slot,
                start,
                end,
                startHour,
                endHour,
                hour,
                inSlot,
            })
            
            return inSlot
        })

        console.log(`[isHourAvailable] Hour in slot check result`, {
            hour,
            hourInSlot,
            slots: daySchedule.slots,
        })

        // If hour is not in provider's availability schedule, block it
        if (!hourInSlot) {
            console.log(`[isHourAvailable] Hour ${hour} not in provider's availability schedule, blocking`)
            return false
        }

        // Check if hour is blocked by existing service requests
        // Only check if schedule data is available and loaded - if not loaded yet, allow the hour
        // (backend will validate on submission)
        // NOTE: We're being permissive here - only block if explicitly marked as fully booked
        // The backend will handle validation of actual blocked hours on submission
        console.log(`[isHourAvailable] Checking available schedule`, {
            scheduleLoading,
            hasAvailableSchedule: !!availableSchedule,
            hasSchedule: !!availableSchedule?.schedule,
            selectedDate,
            scheduleKeys: availableSchedule?.schedule ? Object.keys(availableSchedule.schedule) : [],
        })

        if (!scheduleLoading && availableSchedule?.schedule && selectedDate) {
            const dateSchedule = availableSchedule.schedule[selectedDate]
            console.log(`[isHourAvailable] Date schedule for ${selectedDate}`, {
                dateSchedule,
                isFullyBooked: dateSchedule?.is_fully_booked,
                availableHours: dateSchedule?.available_hours,
                availableHoursType: typeof dateSchedule?.available_hours,
                availableHoursIsArray: Array.isArray(dateSchedule?.available_hours),
                availableHoursLength: dateSchedule?.available_hours?.length,
            })
            
            if (dateSchedule) {
                // Block if day is explicitly marked as fully booked
                if (dateSchedule.is_fully_booked === true) {
                    console.log(`[isHourAvailable] Day ${selectedDate} is fully booked, blocking hour ${hour}`)
                    return false
                }
                
                // Check if hour is in the available_hours list
                // If available_hours exists and is populated, use it as the source of truth
                if (dateSchedule.available_hours && Array.isArray(dateSchedule.available_hours)) {
                    const isInAvailableHours = dateSchedule.available_hours.includes(hour)
                    console.log(`[isHourAvailable] Checking available_hours`, {
                        hour,
                        available_hours: dateSchedule.available_hours,
                        isInAvailableHours,
                    })
                    
                    // If the list is non-empty, block hours that are not in it
                    if (dateSchedule.available_hours.length > 0 && !isInAvailableHours) {
                        console.log(`[isHourAvailable] Hour ${hour} is blocked (not in available_hours)`, {
                            hour,
                            available_hours: dateSchedule.available_hours,
                            selectedDate
                        })
                        return false
                    }
                    
                    // If the list is empty but the day is not fully booked, 
                    // it might mean the schedule hasn't been calculated yet
                    // In this case, we'll still check the provider's availability schedule above
                }
            } else {
                console.log(`[isHourAvailable] No dateSchedule found for ${selectedDate}, allowing hour ${hour} (will be validated on submission)`)
            }
        } else {
            console.log(`[isHourAvailable] Schedule not loaded or not available, allowing hour ${hour} (will be validated on submission)`, {
                scheduleLoading,
                hasAvailableSchedule: !!availableSchedule,
                hasSchedule: !!availableSchedule?.schedule,
            })
        }

        // Allow selecting consecutive hours
        if (selectedHours.length === 0) {
            console.log(`[isHourAvailable] No hours selected yet, allowing hour ${hour}`)
            return true
        }
        
        const sorted = [...selectedHours].sort((a, b) => a - b)
        const isConsecutive = hour === sorted[sorted.length - 1] + 1
        console.log(`[isHourAvailable] Consecutive check`, {
            hour,
            selectedHours,
            sorted,
            lastSelected: sorted[sorted.length - 1],
            nextExpected: sorted[sorted.length - 1] + 1,
            isConsecutive,
        })
        
        return isConsecutive
    }

    const hourlyTotal = useMemo(() => {
        if (!selectedService || selectedService.pricing_type !== 'hourly') return 0
        const rate = Number(selectedService.hourly_rate ?? 0)
        return rate * selectedHours.length
    }, [selectedHours, selectedService])

    const handleBookService = () => {
        if (!selectedService || selectedService.pricing_type !== 'hourly') return

        if (!location?.districtId) {
            toast.error('Configura tu distrito antes de solicitar un servicio.')
            return
        }

        const minimum = selectedService.minimum_hours ?? 1

        if (!selectedDate || selectedHours.length < minimum) {
            toast.error(`Selecciona una fecha y al menos ${minimum} hora(s).`)
            return
        }

        if (!serviceDescription.trim()) {
            toast.error('Describe qué necesitas.')
            return
        }

        const sortedHours = [...selectedHours].sort((a, b) => a - b)
        for (let index = 0; index < sortedHours.length - 1; index += 1) {
            if (sortedHours[index + 1] !== sortedHours[index] + 1) {
                toast.error('Las horas deben ser consecutivas.')
                return
            }
        }

        createServiceRequest(
            {
                offering: selectedService.id,
                description: serviceDescription.trim(),
                location_address: location.address || location.districtName || 'Distrito seleccionado',
                location_latitude: location.latitude ?? null,
                location_longitude: location.longitude ?? null,
                location_reference: '',
                district: location.districtId,
                scheduled_date: selectedDate,
                scheduled_hour: sortedHours[0],
                estimated_duration_hours: sortedHours.length,
            },
            {
                onSuccess: () => {
                    toast.success('Solicitud enviada al proveedor.')
                    navigate(routes.requests)
                },
                onError: (error: any) => {
                    toast.error(
                        error?.response?.data?.detail ||
                            error?.response?.data?.error ||
                            'Ocurrió un error al crear la solicitud.'
                    )
                },
            }
        )
    }

    const handleQuoteFilesChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files?.length) {
            setQuoteFiles([])
            return
        }
        const files = Array.from(event.target.files).slice(0, 5)
        setQuoteFiles(files)
    }

    const handleSendQuote = async () => {
        if (!selectedService || selectedService.pricing_type !== 'quote') return

        if (!location?.districtId) {
            toast.error('Configura tu distrito antes de solicitar una cotización.')
            return
        }

        if (!quoteDescription.trim()) {
            toast.error('Describe lo que necesitas.')
            return
        }

        setQuoteSubmitting(true)
        try {
            const attachmentUrls =
                quoteFiles.length > 0
                    ? await Promise.all(
                          quoteFiles.map((file) => uploadImageToGCS(file, 'quote-references'))
                      )
                    : []

            const quote = await createQuoteRequest.mutateAsync({
                offering: selectedService.id,
                description: quoteDescription.trim(),
                district: location.districtId,
                attachment_urls: attachmentUrls,
            })

            const shouldRequestVisit =
                quoteRequestVisit && !selectedService.requires_quote_visit && quote?.id

            if (shouldRequestVisit) {
                await requestQuoteVisit.mutateAsync({
                    id: quote.id,
                    payload: {
                        visit_notes: quoteVisitNotes,
                    },
                })
            }

            toast.success('Solicitud de cotización enviada.')
            setQuoteDescription('')
            setQuoteFiles([])
            setQuoteVisitNotes('')
            setQuoteRequestVisit(Boolean(selectedService.requires_quote_visit))
        } catch (error: any) {
            toast.error(
                error?.response?.data?.detail ||
                    error?.response?.data?.error ||
                    error?.message ||
                    'No pudimos enviar tu cotización.'
            )
        } finally {
            setQuoteSubmitting(false)
        }
    }

    if (isLoading) {
        return (
            <div className="provider-detail-page">
                <div className="provider-detail-container">
                    <div className="provider-detail-header">
                        <Button variant="primary" onClick={() => navigate(-1)}>
                            <FaArrowLeft /> Atrás
                        </Button>
                    </div>
                    <div className="provider-detail-message">
                        <p>Cargando proveedor...</p>
                    </div>
                </div>
            </div>
        )
    }

    if (isError) {
        return (
            <div className="provider-detail-page">
                <div className="provider-detail-container">
                    <div className="provider-detail-header">
                        <Button variant="primary" onClick={() => navigate(-1)}>
                            <FaArrowLeft /> Atrás
                        </Button>
                    </div>
                    <div className="provider-detail-message error">
                        <p>Ocurrió un error al cargar la información del proveedor.</p>
                    </div>
                </div>
            </div>
        )
    }

    // Allow viewing provider without district, but booking will require district

    if (!provider) {
        return (
            <div className="provider-detail-page">
                <div className="provider-detail-container">
                    <div className="provider-detail-header">
                        <Button variant="primary" onClick={() => navigate(routes.serviceSearch)}>
                            Buscar proveedores
                        </Button>
                    </div>
                    <div className="provider-detail-message">
                        <p>No encontramos este proveedor.</p>
                    </div>
                </div>
            </div>
        )
    }

    const districtsLabel = provider.service_districts?.map((district) => district.name).join(', ')
    const ratingValue = Number(provider.average_rating || 0).toFixed(1)
    const reviewsCount = provider.total_ratings || 0
    const serviceCount = provider.total_services_completed || 0
    // const coverageSummary = location?.districtName || 'Distrito seleccionado'
    const specialtyNames = provider.service_offerings
        .map((service) => service.service_name)
        .filter((name): name is string => Boolean(name))
        .slice(0, 3)
    const specialtyOverflow = Math.max((provider.service_offerings?.length || 0) - specialtyNames.length, 0)

    return (
        <div className="provider-detail-page-wrapper">
            <div className="provider-detail-page">
                <div className="provider-detail-container">
                    <div className="provider-detail-header">
                        <Button variant="secondary" onClick={() => navigate(-1)}>
                            <FaArrowLeft /> Atrás
                        </Button>
                    </div>

                    <div className="provider-detail-grid">
                        <section className="provider-detail-card provider-detail-main">
                            <div className="provider-detail-context-copy">
                                <span className="provider-detail-context-kicker">Agenda sin complicaciones</span>
                                <p>
                                    Coordina servicios profesionales con {provider.first_name}. Revisa los servicios
                                    disponibles, consulta disponibilidad y comparte detalles en minutos.
                                </p>
                            </div>

                            <div className="provider-detail-head">
                                {provider.avatar_url ? (
                                    <img
                                        src={provider.avatar_url}
                                        alt={`Avatar de ${provider.first_name}`}
                                        className="provider-detail-avatar-img"
                                    />
                                ) : (
                                    <div className="provider-detail-avatar">
                                        {(provider.first_name?.[0] || 'P').toUpperCase()}
                                    </div>
                                )}
                                <div className="provider-detail-info">
                                    <span className="provider-detail-kicker">Domber amigo recomendado</span>
                                    <h1 className="provider-detail-name">
                                        {provider.first_name} {provider.last_name}
                                    </h1>

                                    {specialtyNames.length > 0 && (
                                        <div className="provider-detail-specialties">
                                            {specialtyNames.map((label) => (
                                                <span key={label} className="provider-detail-specialty-chip">
                                                    {label}
                                                </span>
                                            ))}
                                            {specialtyOverflow > 0 && (
                                                <span className="provider-detail-specialty-chip provider-detail-specialty-chip--more">
                                                    +{specialtyOverflow} más
                                                </span>
                                            )}
                                        </div>
                                    )}

                                    <div className="provider-detail-metrics">
                                        <div className="provider-detail-metric" aria-label="Calificación del proveedor">
                                            <FaStar />
                                            <div>
                                                <strong>{ratingValue}</strong>
                                                <span>{reviewsCount} reseñas</span>
                                            </div>
                                        </div>
                                        <div className="provider-detail-metric" aria-label="Servicios completados">
                                            <FaCheckCircle />
                                            <div>
                                                <strong>{serviceCount}</strong>
                                                <span>Servicios completados</span>
                                            </div>
                                        </div>
                                        {/* <div className="provider-detail-metric" aria-label="Cobertura del proveedor">
                                            <FaMapMarkerAlt />
                                            <div>
                                                <strong>{coverageSummary}</strong>
                                                <span>Distrito configurado</span>
                                            </div>
                                        </div> */}
                                    </div>

                                    {districtsLabel && (
                                        <div className="provider-detail-coverage">
                                            Atiende en: {districtsLabel}
                                        </div>
                                    )}
                                </div>
                            </div>
                            {provider.bio && <div className="provider-detail-bio">{provider.bio}</div>}
                        </section>

                        <section className="provider-detail-card provider-detail-services-card">
                            <h2 className="provider-detail-section-title">Servicios disponibles</h2>
                            <p className="provider-detail-section-subtitle">
                                Selecciona un servicio para revisar condiciones, tarifas y enviar tu solicitud.
                            </p>
                            <div className="provider-detail-services">
                                {provider.service_offerings.map((service) => {
                                    const isSelected = selectedService?.id === service.id
                                    return (
                                        <button
                                            key={service.id}
                                            type="button"
                                            className={`provider-detail-service ${
                                                isSelected ? 'is-selected' : ''
                                            }`}
                                            onClick={() => setSelectedServiceId(service.id)}
                                        >
                                            <div className="provider-detail-service-header">
                                                <div>
                                                    <div className="provider-detail-service-name">
                                                        {service.service_name}
                                                    </div>
                                                    {service.category_name && (
                                                        <div className="provider-detail-service-category">
                                                            {service.category_name}
                                                        </div>
                                                    )}
                                                </div>
                                                <span
                                                    className={`provider-detail-service-pill provider-detail-service-pill--${service.pricing_type}`}
                                                >
                                                    {service.pricing_type === 'hourly'
                                                        ? 'Cobro por hora'
                                                        : 'Cotización'}
                                                </span>
                                            </div>
                                            {service.pricing_type === 'hourly' && (
                                                <div className="provider-detail-service-meta">
                                                    <strong>
                                                        S/ {Number(service.hourly_rate ?? 0).toFixed(2)}
                                                    </strong>
                                                    <span>
                                                        Mínimo{' '}
                                                        {service.minimum_hours
                                                            ? `${service.minimum_hours} ${
                                                                  service.minimum_hours === 1 ? 'hora' : 'horas'
                                                              }`
                                                            : '1 hora'}
                                                    </span>
                                                </div>
                                            )}
                                            {service.pricing_type === 'quote' && (
                                                <div className="provider-detail-service-meta">
                                                    {service.base_quote_amount != null ? (
                                                        <span>
                                                            Guía desde S/{' '}
                                                            {Number(service.base_quote_amount).toFixed(2)}
                                                        </span>
                                                    ) : (
                                                        <span>El proveedor enviará el monto estimado.</span>
                                                    )}
                                                    {service.requires_quote_visit && (
                                                        <span className="provider-detail-service-warning">
                                                            Requiere visita previa
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                            {service.description && (
                                                <p className="provider-detail-service-desc">
                                                    {service.description}
                                                </p>
                                            )}
                                        </button>
                                    )
                                })}
                            </div>
                        </section>

                        
                    </div>
                    {selectedService?.pricing_type === 'hourly' && (
                            <section className="provider-detail-card provider-detail-action-card">
                                <h2 className="provider-detail-section-title">Reservar por horas</h2>
                                <p className="provider-detail-section-subtitle">
                                    Selecciona fecha y horas consecutivas para enviar tu solicitud con toda la
                                    información necesaria.
                                </p>
                                <div className="provider-detail-info-row">
                                    <FaInfoCircle />
                                    <span>
                                        Precio por hora: S/ {Number(selectedService.hourly_rate ?? 0).toFixed(2)} •{' '}
                                        Mínimo {selectedService.minimum_hours ?? 1}{' '}
                                        {(selectedService.minimum_hours ?? 1) === 1 ? 'hora' : 'horas'}
                                    </span>
                                </div>

                                <div className="provider-detail-scheduler">
                                    <h3>
                                        <FaCalendarAlt /> Selecciona una fecha
                                    </h3>
                                    <div className="provider-detail-datestrip">
                                        {Array.from({ length: 14 }, (_, index) => {
                                            const date = new Date()
                                            date.setDate(date.getDate() + index)
                                            const dateStr = date.toISOString().split('T')[0]
                                            const isSelected = selectedDate === dateStr

                                            const dayKey = date
                                                .toLocaleDateString('en-US', { weekday: 'long' })
                                                .toLowerCase()
                                            const daySchedule = provider.availability_schedule?.[dayKey]
                                            const isAvailableDay =
                                                daySchedule?.enabled && daySchedule?.slots?.length

                                            return (
                                                <button
                                                    key={dateStr}
                                                    type="button"
                                                    className={`provider-detail-date ${
                                                        isSelected ? 'is-selected' : ''
                                                    } ${!isAvailableDay ? 'is-disabled' : ''}`}
                                                    onClick={() => {
                                                        if (!isAvailableDay) return
                                                        setSelectedDate(dateStr)
                                                        setSelectedHours([])
                                                    }}
                                                    disabled={!isAvailableDay}
                                                >
                                                    <span>
                                                        {date.toLocaleDateString('es-ES', { weekday: 'short' })}
                                                    </span>
                                                    <strong>{date.getDate()}</strong>
                                                    <span>
                                                        {date.toLocaleDateString('es-ES', { month: 'short' })}
                                                    </span>
                                                </button>
                                            )
                                        })}
                                    </div>
                                </div>

                                {selectedDate && (
                                    <div className="provider-detail-hours-wrapper">
                                        <h3>
                                            <FaClock /> Selecciona las horas consecutivas
                                        </h3>
                                        <div className="provider-detail-hours">
                                            {hoursRange.map((hour) => {
                                                const isSelected = selectedHours.includes(hour)
                                                const available = isHourAvailable(hour)
                                                return (
                                                    <button
                                                        key={hour}
                                                        type="button"
                                                        className={`provider-detail-hour ${
                                                            isSelected ? 'is-selected' : ''
                                                        } ${!available && !isSelected ? 'is-disabled' : ''}`}
                                                        onClick={() => {
                                                            if (!available && !isSelected) return
                                                            handleHourToggle(hour)
                                                        }}
                                                    >
                                                        {hour.toString().padStart(2, '0')}:00
                                                    </button>
                                                )
                                            })}
                                        </div>
                                        {selectedHours.length > 0 && (
                                            <div className="provider-detail-hours-selected">
                                                <FaCheckCircle /> {selectedHours.length} hora(s) seleccionada(s)
                                            </div>
                                        )}
                                    </div>
                                )}

                                {selectedHours.length > 0 && (
                                    <div className="provider-detail-notes">
                                        <Input
                                            name="serviceDescription"
                                            label="Describe qué necesitas"
                                            multiline
                                            rows={4}
                                            placeholder="Cuéntale al proveedor los detalles de tu solicitud..."
                                            value={serviceDescription}
                                            setValue={setServiceDescription}
                                        />
                                    </div>
                                )}

                                <div className="provider-detail-summary">
                                    <div>
                                        <span>Total estimado</span>
                                        <strong>S/ {hourlyTotal.toFixed(2)}</strong>
                                    </div>
                                    <Button
                                        variant="primary"
                                        onClick={handleBookService}
                                        disabled={isCreatingRequest || selectedHours.length === 0}
                                    >
                                        {isCreatingRequest ? 'Enviando...' : 'Reservar servicio'}
                                    </Button>
                                </div>
                            </section>
                        )}

                        {selectedService?.pricing_type === 'quote' && (
                            <section className="provider-detail-card provider-detail-action-card">
                                <h2 className="provider-detail-section-title">Solicitar cotización</h2>
                                <p className="provider-detail-section-subtitle">
                                    Describe el trabajo y adjunta referencias para recibir una propuesta personalizada.
                                </p>
                                <div className="provider-detail-info-row">
                                    <FaInfoCircle />
                                    <span>
                                        Comparte detalles, horarios y referencias para que {provider.first_name} pueda
                                        cotizar con precisión.
                                    </span>
                                </div>

                                <Input
                                    name="quoteDescription"
                                    label="Descripción del trabajo"
                                    multiline
                                    rows={6}
                                    placeholder="Explica el trabajo, los materiales, horarios o cualquier detalle importante..."
                                    value={quoteDescription}
                                    setValue={setQuoteDescription}
                                />

                                <div className="provider-detail-attachments">
                                    <label className="provider-detail-file-label">
                                        <FaFileImage /> Sube fotos de referencia (opcional)
                                        <input
                                            type="file"
                                            accept="image/*"
                                            multiple
                                            onChange={handleQuoteFilesChange}
                                        />
                                    </label>
                                    {quoteFiles.length > 0 && (
                                        <ul className="provider-detail-file-list">
                                            {quoteFiles.map((file) => (
                                                <li key={file.name}>{file.name}</li>
                                            ))}
                                        </ul>
                                    )}
                                </div>

                                <div className="provider-detail-visit">
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={selectedService.requires_quote_visit ? true : quoteRequestVisit}
                                            onChange={(event) => setQuoteRequestVisit(event.target.checked)}
                                            disabled={selectedService.requires_quote_visit}
                                        />
                                        {selectedService.requires_quote_visit
                                            ? 'Este servicio requiere una visita antes de enviar la cotización.'
                                            : 'Quiero solicitar una visita para revisar detalles antes de la cotización.'}
                                    </label>

                                    {(selectedService.requires_quote_visit || quoteRequestVisit) && (
                                        <Input
                                            name="quoteVisitNotes"
                                            label="Notas para la visita (opcional)"
                                            multiline
                                            rows={3}
                                            placeholder="Indica horarios disponibles u observaciones para la visita."
                                            value={quoteVisitNotes}
                                            setValue={setQuoteVisitNotes}
                                        />
                                    )}
                                </div>

                                <div className="provider-detail-summary">
                                    <div>
                                        {selectedService.base_quote_amount != null ? (
                                            <span>
                                                Monto referencial: S/{' '}
                                                {Number(selectedService.base_quote_amount).toFixed(2)}
                                            </span>
                                        ) : (
                                            <span>Recibirás la cotización exacta del proveedor.</span>
                                        )}
                                    </div>
                                    <Button
                                        variant="primary"
                                        onClick={handleSendQuote}
                                        disabled={quoteSubmitting}
                                    >
                                        {quoteSubmitting ? 'Enviando...' : 'Enviar solicitud de cotización'}
                                    </Button>
                                </div>
                            </section>
                        )}
                </div>
            </div>
        </div>
    )
}

export default ProviderDetail

